import { CoreFetch } from '../../api/index';
import { ClaimApi } from '../../api/server/apis/ClaimApi';
import {
    ClearinghouseApi,
    ListProviderClearinghouseErasOrderEnum,
    ListSupportClearinghouseErasOrderEnum,
} from '../../api/server/apis/ClearinghouseApi';
import { DocumentApi } from '../../api/server/apis/DocumentApi';
import {
    ExplanationOfBenefitsApi,
    ListProviderEOBsOrderEnum,
    ListSupportEOBsOrderEnum,
} from '../../api/server/apis/ExplanationOfBenefitsApi';
import { ProviderClaimPayment } from '../../api/server/models/ProviderClaimPayment';
import { ProviderClearinghouseEra } from '../../api/server/models/ProviderClearinghouseEra';
import { ProviderClearinghouseEraItem } from '../../api/server/models/ProviderClearinghouseEraItem';
import { ProviderClearinghouseEraSortType } from '../../api/server/models/ProviderClearinghouseEraSortType';
import { ProviderEOB } from '../../api/server/models/ProviderEOB';
import { ProviderEOBSortType } from '../../api/server/models/ProviderEOBSortType';
import { SupportClearinghouseEraSortType } from '../../api/server/models/SupportClearinghouseEraSortType';
import { SupportEOBSortType } from '../../api/server/models/SupportEOBSortType';

/**
 * Everything the Remits page reads, behind one interface (FE-163) — the Denials pattern: the provider console and
 * Support call different endpoints for the same data, and the page must not know which.
 *
 * ## Support lists active remits only
 *
 * `/support/clearinghouse/era` and `/support/eob` include DISABLED remits; the provider endpoints do not. Measured on
 * production 2026-10-08 for one account: 3,903 active + 106 disabled = 4,009 ERAs, 2,516 + 207 = 2,723 EOBs, and
 * the provider endpoints answered 3,903 and 2,516. Support sends `filterActive=EQ:1` so both consoles show one set.
 *
 * The spelling matters, measured 2026-10-09: a bare `1` or `true` is a 400 ("missing operator"); `EQ:1` / `EQ:0`
 * partition the list exactly (3,920 + 106 = 4,026 ERAs, 2,516 + 207 = 2,723 EOBs); and `EQ:true` returns the DISABLED
 * set — a filter that reads right and inverts silently.
 */
const ACTIVE_ONLY = 'EQ:1';

export type RemitKind = 'ERA' | 'EOB';

/** The list's filters (crosswalk §1). All optional; amounts and dates are as typed, the API compares them. */
export interface IRemitQuery {
    limit: number;
    offset: number;
    clearinghouseId?: string;
    payerName?: string;
    /** Part of the payment number, as typed. */
    paymentNumber?: string;
    /** Exactly this payment number — Reconcile's "Same payment number" (FE-184), which a part would over-match. */
    paymentNumberExact?: string;
    /** The paid amount, exactly. */
    amount?: string;
    paidFrom?: string;
    paidTo?: string;
    /** The paid amount within a band, both ends included — Reconcile's "within $5" of a deposit (crosswalk §5). */
    amountBetween?: [string, string];
    /** Posted to a bank deposit (`true`) or not yet (`false`); either when unset. */
    posted?: boolean;
    /** Remits posted to any of these bank transactions. Repeated `EQ:` values are OR'ed by the API — what this needs. */
    transactionIds?: string[];
    /** These remits only — a write is read back by its id. */
    ids?: string[];
}

/** One row of either list, in one shape. */
export interface IRemit {
    kind: RemitKind;
    /** The record's id — what every follow-up call takes. */
    id: string;
    /** What the page shows as "ERA ID" / "EOB ID": the ERA's `eraId` (TRN02), the EOB's own id. */
    displayId: string;
    payerName?: string;
    paidAmount?: string;
    paymentType?: string;
    paidDate?: string;
    /** "Received" for an ERA, "Uploaded" for an EOB: when ENTER got it. */
    created?: string;
    providerName?: string;
    paymentNumber?: string;
    npi?: string;
    ein?: string;
    clearinghouseId?: string;
    plaidItemTransactionId?: string;
    /** The ERA's stored 835 — the provider adjustments are read from it (crosswalk §2). */
    x12?: string;
    /** The EOB's supporting document. */
    documentId?: string;
    /** The EOB record, kept for its provider adjustments. */
    eob?: ProviderEOB;
}

export interface IRemitsApi {
    listRemits(kind: RemitKind, query: IRemitQuery): Promise<{ rows: IRemit[]; total: number }>;
    /** An ERA's items, every page / an EOB's payments. */
    listClaims(remit: IRemit): Promise<Array<ProviderClearinghouseEraItem | ProviderClaimPayment>>;
    listClearinghouses(): Promise<Array<{ id: string; name: string; type?: string; created?: string }>>;
    /** A short-lived URL for the remit's PDF, or undefined when it has none. */
    remitPdfUrl(remit: IRemit): Promise<string | undefined>;
    /** A short-lived URL for one ERA claim's PDF. */
    eraClaimPdfUrl(itemId: string): Promise<string | undefined>;
    /**
     * The claim a payment settles, with its patient — through the payment, never `professionalId` (FE-116, measured
     * 2026-10-01). For View Claim, and for an EOB's patient, which the payment does not carry (crosswalk §2).
     */
    claimForPayment(paymentId: string): Promise<IRemitClaimLink | undefined>;
    /** `POST …/clearinghouse/{id}/era/x12/5010/835`. Not retried: a retry after a timeout could create a second ERA. */
    uploadEra(clearinghouseId: string, x12: string): Promise<string | undefined>;
    /**
     * Posts the remit to a bank transaction, or unposts it with `null` (crosswalk §5: the update's one field,
     * `plaidItemTransactionId`). Not retried. The caller reads the remit back: a 200 is not proof (NS-17).
     *
     * Measured on production 2026-10-12 (Support): the server allows ONE remit per bank transaction — linking a
     * transaction that is already linked, even to the same remit, is a 500 with
     * `err.banking_account_transaction_already_correlated_to_clearinghouse_era`. Unpost is `null`, which the spec does
     * not say (the field is a non-nullable string): proven on production (NS-17) — Support ERA 2026-10-12, provider ERA
     * and provider EOB 2026-10-13 — and it removes only links posted by hand; automatic matches stay (backend).
     */
    linkTransaction(remit: IRemit, transactionId: string | null): Promise<void>;
}

export interface IRemitClaimLink {
    id: string;
    patient?: string;
}

const linkOf = (claim: { id?: string; patientFirst?: string; patientLast?: string } | undefined): IRemitClaimLink | undefined =>
    claim?.id
        ? {
              id: claim.id,
              patient: [claim.patientLast, claim.patientFirst].map(part => (part || '').trim()).filter(Boolean).join(', ') || undefined,
          }
        : undefined;

const fromEra = (era: ProviderClearinghouseEra): IRemit => ({
    kind: 'ERA',
    id: era.id || '',
    displayId: era.eraId || era.id || '',
    payerName: era.payerName,
    paidAmount: era.paidAmount?.amount,
    paymentType: era.paymentType,
    paidDate: era.paidDate,
    created: era.created,
    providerName: era.providerName,
    paymentNumber: era.paymentNumber,
    npi: era.npi,
    ein: era.ein,
    clearinghouseId: era.clearinghouseId,
    plaidItemTransactionId: era.plaidItemTransactionId,
    x12: era.x12,
});

const fromEob = (eob: ProviderEOB): IRemit => ({
    kind: 'EOB',
    id: eob.id || '',
    displayId: eob.id || '',
    payerName: eob.payerName,
    paidAmount: eob.paidAmount?.amount,
    paymentType: eob.paymentType,
    paidDate: eob.paidDate,
    created: eob.created,
    paymentNumber: eob.paymentNumber,
    documentId: eob.documentId,
    plaidItemTransactionId: eob.plaidItemTransactionId,
    eob,
});

/**
 * The paid-date filter. A range is ONE value, `BT:from:to` — the spelling the shared data table already uses.
 *
 * Not two: repeated values of one filter are OR'ed, not AND'ed. Measured on production 2026-10-09,
 * `filterPaidDate=GTE:2026-09-01&filterPaidDate=LTE:2026-09-30` answered 3,919 of 3,920 ERAs; `BT:2026-09-01:2026-09-30`
 * answered September's 505, the same as `count(≥ Sep 1) − count(≥ Oct 1)`. The crosswalk's "repeat the param" is wrong.
 */
export const paidDateFilter = (paidFrom?: string, paidTo?: string): string[] =>
    paidFrom && paidTo
        ? [`BT:${paidFrom}:${paidTo}`]
        : paidFrom
        ? [`GTE:${paidFrom}`]
        : paidTo
        ? [`LTE:${paidTo}`]
        : [];

/**
 * Which bank deposit the remit is posted to (crosswalk §5): one of the given transactions, any (`NEMPTY`), or none yet
 * (`EMPTY`). Measured 2026-10-09: EMPTY + NEMPTY partition the list, combined with an amount filter as well.
 */
export const postedFilter = (query: Pick<IRemitQuery, 'posted' | 'transactionIds'>): string[] | undefined =>
    query.transactionIds?.length
        ? query.transactionIds.map(id => `EQ:${id}`)
        : query.posted === true
        ? ['NEMPTY']
        : query.posted === false
        ? ['EMPTY']
        : undefined;

/** The crosswalk's filters, as both list endpoints of both consoles spell them. */
const filters = (query: IRemitQuery) => ({
    limit: query.limit,
    offset: query.offset,
    filterPayerName: query.payerName ? [`LK:${query.payerName}`] : undefined,
    filterControlNumber: query.paymentNumberExact
        ? [`EQ:${query.paymentNumberExact}`]
        : query.paymentNumber
        ? [`LK:${query.paymentNumber}`]
        : undefined,
    filterPayerPaid: query.amount
        ? [`EQ:${query.amount}`]
        : query.amountBetween
        ? [`BT:${query.amountBetween[0]}:${query.amountBetween[1]}`]
        : undefined,
    filterPaidDate: paidDateFilter(query.paidFrom, query.paidTo),
    filterPlaidItemTransactionId: postedFilter(query),
    filterId: query.ids?.length ? query.ids.map(id => `EQ:${id}`) : undefined,
});
const withoutEmpty = <T extends object>(request: T): T =>
    Object.fromEntries(
        Object.entries(request).filter(([, value]) => value !== undefined && !(Array.isArray(value) && !value.length)),
    ) as T;

const totalOf = (response: { raw: Response }, rows: unknown[]): number => {
    const total = Number(response.raw.headers.get('pagination-total'));
    return Number.isFinite(total) && response.raw.headers.has('pagination-total') ? total : rows.length;
};

/** Every page of an ERA's items. An ERA's claim count is small; the loop is a safety net, not a crawl. */
const allPages = async <T>(page: (offset: number) => Promise<T[]>): Promise<T[]> => {
    const all: T[] = [];
    for (let offset = 0; offset < 2000; offset += 100) {
        const rows = await page(offset);
        all.push(...rows);
        if (rows.length < 100) {
            break;
        }
    }
    return all;
};

/**
 * Paging needs a total order, and no list sorts by id, so paid date alone leaves ties that offset paging skips and repeats.
 * Measured on production 2026-10-12, reading 100 at a time: September's 505 ERAs gave 502 distinct by paid date, 504 with
 * created added, all 505 with created and amount; 2,000 EOBs gave 1,966 → 2,000. The backend is adding a tie-breaker of its
 * own; until then the extra keys are what keeps a remit from vanishing between pages.
 */
const byPaidDate = <S, O>(keys: { PAIDDATE: S; CREATED: S; PAIDAMOUNT: S }, orders: { DESCENDING: O }): { sort: S[]; order: O[] } => ({
    sort: [keys.PAIDDATE, keys.CREATED, keys.PAIDAMOUNT],
    order: [orders.DESCENDING, orders.DESCENDING, orders.DESCENDING],
});

const providerPage = async (kind: RemitKind, query: IRemitQuery): Promise<{ rows: IRemit[]; total: number }> => {
    if (kind === 'ERA') {
        const response = await CoreFetch<ClearinghouseApi>(ClearinghouseApi).listProviderClearinghouseErasRaw(
            withoutEmpty({
                ...filters(query),
                filterClearinghouseId: query.clearinghouseId ? [`EQ:${query.clearinghouseId}`] : undefined,
                ...byPaidDate(ProviderClearinghouseEraSortType, ListProviderClearinghouseErasOrderEnum),
            }),
        );
        const rows = await response.value();
        return { rows: rows.map(fromEra), total: totalOf(response, rows) };
    }
    const response = await CoreFetch<ExplanationOfBenefitsApi>(ExplanationOfBenefitsApi).listProviderEOBsRaw(
        withoutEmpty({
            ...filters(query),
            ...byPaidDate(ProviderEOBSortType, ListProviderEOBsOrderEnum),
        }),
    );
    const rows = await response.value();
    return { rows: rows.map(fromEob), total: totalOf(response, rows) };
};

export const providerRemitsApi: IRemitsApi = {
    listRemits: providerPage,
    listClaims: async remit =>
        remit.kind === 'ERA'
            ? allPages(offset =>
                  CoreFetch<ClearinghouseApi>(ClearinghouseApi).listProviderClearinghouseEraItems({
                      filterClearinghouseEraId: [`EQ:${remit.id}`],
                      limit: 100,
                      offset,
                  }),
              )
            : CoreFetch<ExplanationOfBenefitsApi>(ExplanationOfBenefitsApi).getProviderEOBPayments({ id: remit.id }),
    listClearinghouses: async () =>
        (await allPages(offset => CoreFetch<ClearinghouseApi>(ClearinghouseApi).listProviderClearinghouses({ limit: 100, offset }))).map(c => ({
            id: c.id || '',
            name: c.name || '',
            type: c.type,
            created: c.created,
        })),
    remitPdfUrl: async remit =>
        remit.kind === 'ERA'
            ? (await CoreFetch<DocumentApi>(DocumentApi).viewProviderClearinghouseEraExplanationOfBenefitsDocument({ id: remit.id })).url
            : remit.documentId
            ? (await CoreFetch<DocumentApi>(DocumentApi).getProviderDocumentUrl({ id: remit.documentId })).url
            : undefined,
    eraClaimPdfUrl: async itemId =>
        (await CoreFetch<DocumentApi>(DocumentApi).viewProviderClearinghouseEraItemExplanationOfBenefitsDocument({ id: itemId })).url,
    claimForPayment: async paymentId =>
        linkOf((await CoreFetch<ClaimApi>(ClaimApi).listProviderClaims({ filterPaymentId: [`EQ:${paymentId}`], limit: 5 }))[0]),
    uploadEra: async (clearinghouseId, x12) =>
        (await CoreFetch<ClearinghouseApi>(ClearinghouseApi).postProviderX12835Era({ id: clearinghouseId, body: x12 })).id,
    linkTransaction: async (remit, transactionId) => {
        // `null` must reach the server as null: the generated model types the field `string | undefined`, and an
        // undefined field is dropped from the body — which would turn Unpost into a request that changes nothing.
        const plaidItemTransactionId = transactionId as string;
        if (remit.kind === 'ERA') {
            await CoreFetch<ClearinghouseApi>(ClearinghouseApi).updateProviderClearinghouseEra({
                id: remit.id,
                providerUpdateClearinghouseEraPayload: { plaidItemTransactionId },
            });
        } else {
            await CoreFetch<ExplanationOfBenefitsApi>(ExplanationOfBenefitsApi).updateProviderEob({
                id: remit.id,
                providerUpdateEOBPayload: { plaidItemTransactionId },
            });
        }
    },
};

const supportPage = async (kind: RemitKind, query: IRemitQuery): Promise<{ rows: IRemit[]; total: number }> => {
    if (kind === 'ERA') {
        const response = await CoreFetch<ClearinghouseApi>(ClearinghouseApi).listSupportClearinghouseErasRaw(
            withoutEmpty({
                ...filters(query),
                filterActive: ACTIVE_ONLY,
                filterClearinghouseId: query.clearinghouseId ? [`EQ:${query.clearinghouseId}`] : undefined,
                ...byPaidDate(SupportClearinghouseEraSortType, ListSupportClearinghouseErasOrderEnum),
            }),
        );
        const rows = await response.value();
        return { rows: rows.map(fromEra), total: totalOf(response, rows) };
    }
    const response = await CoreFetch<ExplanationOfBenefitsApi>(ExplanationOfBenefitsApi).listSupportEOBsRaw(
        withoutEmpty({
            ...filters(query),
            filterActive: ACTIVE_ONLY,
            ...byPaidDate(SupportEOBSortType, ListSupportEOBsOrderEnum),
        }),
    );
    const rows = await response.value();
    return { rows: rows.map(fromEob), total: totalOf(response, rows) };
};

export const supportRemitsApi: IRemitsApi = {
    listRemits: supportPage,
    listClaims: async remit =>
        remit.kind === 'ERA'
            ? allPages(offset =>
                  CoreFetch<ClearinghouseApi>(ClearinghouseApi).listSupportClearinghouseEraItems({
                      filterClearinghouseEraId: [`EQ:${remit.id}`],
                      limit: 100,
                      offset,
                  }),
              )
            : CoreFetch<ExplanationOfBenefitsApi>(ExplanationOfBenefitsApi).getSupportEOBPayments({ id: remit.id }),
    listClearinghouses: async () =>
        (await allPages(offset => CoreFetch<ClearinghouseApi>(ClearinghouseApi).listSupportClearinghouses({ limit: 100, offset }))).map(c => ({
            id: c.id || '',
            name: c.name || '',
            type: c.type,
            created: c.created,
        })),
    remitPdfUrl: async remit =>
        remit.kind === 'ERA'
            ? (await CoreFetch<DocumentApi>(DocumentApi).viewSupportClearinghouseEraExplanationOfBenefitsDocument({ id: remit.id })).url
            : remit.documentId
            ? (await CoreFetch<DocumentApi>(DocumentApi).getSupportDocumentUrl({ id: remit.documentId })).url
            : undefined,
    eraClaimPdfUrl: async itemId =>
        (await CoreFetch<DocumentApi>(DocumentApi).viewSupportClearinghouseEraItemExplanationOfBenefitsDocument({ id: itemId })).url,
    claimForPayment: async paymentId =>
        linkOf((await CoreFetch<ClaimApi>(ClaimApi).listSupportClaims({ filterPaymentId: [`EQ:${paymentId}`], limit: 5 }))[0]),
    uploadEra: async (clearinghouseId, x12) =>
        (await CoreFetch<ClearinghouseApi>(ClearinghouseApi).postX12835Era({ id: clearinghouseId, body: x12 })).id,
    linkTransaction: async (remit, transactionId) => {
        const plaidItemTransactionId = transactionId as string; // null stays null — see the provider's version
        if (remit.kind === 'ERA') {
            await CoreFetch<ClearinghouseApi>(ClearinghouseApi).updateSupportClearinghouseEra({
                id: remit.id,
                supportUpdateClearinghouseEraPayload: { plaidItemTransactionId },
            });
        } else {
            // Support's EOB update carries every EOB field, and whether the server patches or replaces is unknown (sandbox
            // cannot answer: its EOB list is a 500). Sending the record as read, with the one field changed, is right either
            // way — nothing absent to be cleared.
            const eob = remit.eob || {};
            await CoreFetch<ExplanationOfBenefitsApi>(ExplanationOfBenefitsApi).updateSupportEob({
                id: remit.id,
                supportUpdateEOBPayload: {
                    payerName: eob.payerName,
                    paymentType: eob.paymentType,
                    paymentNumber: eob.paymentNumber,
                    paidAmount: eob.paidAmount,
                    paidDate: eob.paidDate,
                    documentId: eob.documentId,
                    providerAdjustments: eob.providerAdjustments,
                    plaidItemTransactionId,
                },
            });
        }
    },
};
